import React from 'react';
import { Brain, RefreshCw } from 'lucide-react';
import { DataTable } from '../../common/DataTable';
import Card from '../../common/Card';
import Badge from '../../common/Badge';
import EmptyState from '../../common/EmptyState';
import Button from '../../common/Button';
import { COMPLEXITY_TONE } from '../parts/tones';
import CorvinArt from '../../brand/CorvinArt';

// Extracted verbatim from JobManagement's diagnostics tab block (Task 7),
// restyled to B2 primitives (Task 5 of the mechanic UI restyle).
// Presentational only — loadAllDiagnostics comes from useDiagnostics via
// the orchestrator; this tab calls no API itself.
//
// Note (plan discrepancy, reported): the plan expected the diagnostic-detail
// modal to be nested inside this block. It is actually a sibling of it in
// the orchestrator's JSX, so it stays there and Task 8 extracts it directly.
const DiagnosticsTab = ({
  allDiagnostics,
  diagnosticsPage,
  setDiagnosticsPage,
  diagnosticsTotalPages,
  diagnosticsLoading,
  setSelectedDiagnostic,
  loadAllDiagnostics,
  setShowDiagnosticDetail
}) => {
  const columns = [
    {
      header: 'Job',
      accessor: 'job_number',
      render: (diag) => (
        <div>
          <div className="font-mono tabular-nums text-ink font-medium">{diag.job_number}</div>
          <div className="mt-0.5 text-label uppercase text-ink-muted">{diag.job_status}</div>
        </div>
      ),
    },
    {
      header: 'Customer / Vehicle',
      accessor: 'customer_name',
      render: (diag) => (
        <div>
          <div className="text-ink">{diag.customer_name}</div>
          <div className="text-small text-ink-muted">{diag.vehicle || '—'}</div>
        </div>
      ),
    },
    {
      header: 'Primary Diagnosis',
      accessor: 'possible_causes',
      sortable: false,
      render: (diag) => {
        const causes = diag.possible_causes || [];
        const first = causes[0];
        const causeText = typeof first === 'string' ? first : first?.cause || '—';
        return (
          <div>
            <span className="block max-w-xs truncate text-ink-soft" title={causeText}>
              {causeText}
            </span>
            {causes.length > 1 && (
              <div className="text-small text-ink-muted">+{causes.length - 1} more causes</div>
            )}
          </div>
        );
      },
    },
    {
      header: 'Confidence',
      accessor: 'confidence_score',
      numeric: true,
      render: (diag) => (
        <span className="font-mono tabular-nums text-ink">
          {Math.round((diag.confidence_score || 0) * 100)}%
        </span>
      ),
    },
    {
      header: 'Complexity',
      accessor: 'repair_complexity',
      render: (diag) => (
        <Badge status={COMPLEXITY_TONE[diag.repair_complexity] || 'neutral'}>
          {diag.repair_complexity || '—'}
        </Badge>
      ),
    },
    {
      header: 'Est. Cost',
      accessor: 'estimated_parts_cost',
      numeric: true,
      render: (diag) => (
        <div className="font-mono tabular-nums text-right">
          <div className="text-ink">
            ${(diag.estimated_parts_cost + diag.estimated_labor_hours * 100).toFixed(2)}
          </div>
          <div className="text-small text-ink-muted">{diag.estimated_labor_hours}h labor</div>
        </div>
      ),
    },
    {
      header: 'Date',
      accessor: 'created_at',
      render: (diag) => (
        <span className="font-mono tabular-nums text-ink-soft">
          {diag.created_at ? new Date(diag.created_at).toLocaleDateString() : 'N/A'}
        </span>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      sortable: false,
      render: (diag) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setSelectedDiagnostic(diag); setShowDiagnosticDetail(true); }}
        >
          View Details
        </Button>
      ),
    },
  ];

  const renderDiagnosticCard = (diag) => {
    const causes = diag.possible_causes || [];
    const first = causes[0];
    return (
      <Card className="p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono tabular-nums text-ink font-medium">{diag.job_number}</span>
          <Badge status={COMPLEXITY_TONE[diag.repair_complexity] || 'neutral'}>
            {diag.repair_complexity || '—'}
          </Badge>
        </div>
        <div className="mt-1 text-small text-ink-muted">
          {diag.customer_name} &middot; {diag.vehicle || '—'}
        </div>
        <p className="mt-2 text-body text-ink-soft">
          {typeof first === 'string' ? first : first?.cause || '—'}
        </p>
        <div className="mt-2 font-mono tabular-nums text-small text-ink">
          {Math.round((diag.confidence_score || 0) * 100)}% confidence
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono tabular-nums text-small text-ink-muted">
          <span>
            ${(diag.estimated_parts_cost + diag.estimated_labor_hours * 100).toFixed(2)} &middot; {diag.estimated_labor_hours}h labor
          </span>
          <span>{diag.created_at ? new Date(diag.created_at).toLocaleDateString() : 'N/A'}</span>
        </div>
        <div className="mt-3">
          <Button
            variant="secondary"
            size="sm"
            className="min-h-touch w-full"
            onClick={() => { setSelectedDiagnostic(diag); setShowDiagnosticDetail(true); }}
          >
            View Details
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-surface-card rounded-card shadow-elevation-1 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CorvinArt pose="avatar" size={40} className="mr-3" />
            <div>
              <h2 className="text-section-title font-bold text-ink">AI Diagnostic History</h2>
              <p className="text-sm text-ink-muted">View all AI-powered diagnostic analyses across your jobs</p>
            </div>
          </div>
          {/* Secondary: the one solid accent button on this screen is "New Job". */}
          <Button variant="secondary" icon={RefreshCw} loading={diagnosticsLoading} onClick={loadAllDiagnostics}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Diagnostics List */}
      <div className="bg-surface-card rounded-card shadow-elevation-1">
        {allDiagnostics.length === 0 && !diagnosticsLoading ? (
          <EmptyState
            icon={Brain}
            title="No diagnostics yet"
            description="Run a diagnosis from a job to see AI analysis here."
          />
        ) : (
          <DataTable
            data={allDiagnostics}
            columns={columns}
            density="compact"
            loading={diagnosticsLoading}
            pagination={false}
            filterable={false}
            sortable={false}
            renderCard={renderDiagnosticCard}
          />
        )}

        {/* Pagination */}
        {diagnosticsTotalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border-default">
            <button
              onClick={() => setDiagnosticsPage(p => Math.max(1, p - 1))}
              disabled={diagnosticsPage === 1}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-ink-muted">
              Page {diagnosticsPage} of {diagnosticsTotalPages}
            </span>
            <button
              onClick={() => setDiagnosticsPage(p => Math.min(diagnosticsTotalPages, p + 1))}
              disabled={diagnosticsPage === diagnosticsTotalPages}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticsTab;
